// =============================================================================
// CATALOGUE - Gestion des produits, services et packs
// =============================================================================
// Inclut : sélection multiple + actions groupées + filtres avancés
// =============================================================================
import { useState, useEffect } from "react";
import { OHGrid } from "../../components/ui/oh-grid";
import { isInstallateurProfile, hasAmoMissions, hasIntegrationPennylane } from "../../utils/tenantProfile";
// 🔑 N°889 — source UNIQUE du formatage d'un 422 (3 sites la partageaient en
// double dans cet écran, et d'autres écrans portent la même faute).
import { messageErreurValidation, champsObligatoiresManquants, refSaisieOuOmise } from "../../utils/erreursValidation";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Package, 
  Wrench, 
  Boxes, 
  Plus, 
  Search, 
  Edit, 
  Loader2,
  Euro,
  RefreshCw,
  Download,
  Upload,
  FileSpreadsheet
} from "lucide-react";
import SmartLegalEditor from "../../components/editors/SmartLegalEditor";
import MissionsAMOTab from "./MissionsAMOTab";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import {  // 🔴 OMV-12 / N°980 — specs techniques (source unique : affichage + édition)
  CARACT_LABELS, caracteristiquesListe, caracteristiquesVersLignes, lignesVersCaracteristiques,
} from "./catalogueCaracteristiques";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour construire les headers avec authentification
const getAuthHeaders = (tenantId) => {
  const token = localStorage.getItem('session_token');
  return {
    "X-Tenant-ID": tenantId,
    "Content-Type": "application/json",
    ...(token && { "Authorization": `Bearer ${token}` })
  };
};

export default function CataloguePage({ tenantId = "jkc" }) {
  const isOmv = isInstallateurProfile();  // profil installateur (OMV/Climat) : masque Missions AMO (concept AMO/TEF)
  // 🔴 L'onglet Missions AMO dépend d'une CAPABILITÉ DÉCLARÉE,
  // ⛔ plus d'une exclusion. Absente ⇒ on ⛔ n'affiche PAS.
  const peutVoirMissionsAmo = hasAmoMissions();
  const { items: catProduitItems } = useConfigList("categories_produit");
  const { items: catServiceItems } = useConfigList("categories_service");
  const { items: typesProduitItems } = useConfigList("types_produit");
  const { items: typesServiceItems } = useConfigList("types_service");
  const [activeTab, setActiveTab] = useState("produits");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [produits, setProduits] = useState([]);
  const [services, setServices] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("all");
  
  // Dialog states
  const [showProduitDialog, setShowProduitDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [fichesCEE, setFichesCEE] = useState([]);

  // Charger les données
  useEffect(() => {
    loadCatalogue();
    loadFichesCEE();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadFichesCEE = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API_URL}/api/metier/aides/fiches-cee`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Transformer en format pour le Select
        const options = data.map(f => ({
          value: f.code,
          label: `${f.code} - ${f.nom}`
        }));
        setFichesCEE(options);
      }
    } catch (error) {
      console.error("Erreur chargement fiches CEE:", error);
    }
  };

  const loadCatalogue = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders(tenantId);
      
      // actif_only=false : charger AUSSI les produits/services désactivés pour qu'ils
      // restent visibles dans la grille (badge "Inactif" + filtre Statut, défaut "Tous").
      // Sans ça, un produit désactivé disparaissait (= "fantôme").
      const [produitsRes, servicesRes, packsRes] = await Promise.all([
        fetch(`${API_URL}/api/metier/catalogue/produits?actif_only=false`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/metier/catalogue/services?actif_only=false`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/metier/catalogue/packs`, { headers, credentials: 'include' })
      ]);

      if (produitsRes.ok) {
        const produitsData = await produitsRes.json();
        console.log("Produits chargés:", produitsData.length, "items");
        setProduits(produitsData);
      } else {
        console.error("Erreur chargement produits:", produitsRes.status);
      }
      if (servicesRes.ok) setServices(await servicesRes.json());
      if (packsRes.ok) setPacks(await packsRes.json());
    } catch (error) {
      console.error("Erreur chargement catalogue:", error);
      toast.error("Erreur lors du chargement du catalogue");
    } finally {
      setLoading(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const items = activeTab === "produits" ? produits : services;
    const type = activeTab === "produits" ? "produits" : "services";
    
    if (items.length === 0) {
      toast.warning("Aucun élément à exporter");
      return;
    }

    // Déterminer les colonnes
    const columns = activeTab === "produits" 
      ? ["reference", "nom", "categorie", "marque", "modele", "prix_ht", "taux_tva", "fiche_cee", "actif", "description"]
      : ["reference", "nom", "categorie", "prix_ht", "taux_tva", "unite", "actif", "description"];

    // En-tête CSV
    let csv = columns.join(";") + "\n";

    // Données
    items.forEach(item => {
      const row = columns.map(col => {
        let val = item[col] ?? "";
        if (typeof val === "boolean") val = val ? "oui" : "non";
        if (typeof val === "string" && (val.includes(";") || val.includes("\n"))) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      });
      csv += row.join(";") + "\n";
    });

    // Télécharger
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `catalogue_${type}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`${items.length} ${type} exportés`);
  };

  // Import CSV
  const handleImportCSV = async () => {
    if (!importFile) return;

    setImporting(true);
    try {
      const text = await importFile.text();
      const lines = text.split("\n").filter(l => l.trim());
      
      if (lines.length < 2) {
        toast.error("Fichier CSV vide ou invalide");
        return;
      }

      // Parser l'en-tête
      const headers = lines[0].split(";").map(h => h.trim().toLowerCase().replace(/"/g, ""));
      
      // Parser les lignes
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(";").map(v => v.trim().replace(/^"|"$/g, ""));
        const item = {};
        headers.forEach((h, idx) => {
          let val = values[idx] || "";
          if (h === "actif") {
            // Colonne actif vide/absente → actif par défaut (true). Seules les
            // valeurs explicitement négatives (non/false/0) désactivent.
            const t = val.trim().toLowerCase();
            val = t === "" || t === "oui" || t === "true" || t === "1";
          }
          if (h === "prix_ht" || h === "taux_tva") val = parseFloat(val) || 0;
          item[h] = val;
        });
        if (item.nom) items.push(item);
      }

      // Envoyer au backend
      const endpoint = activeTab === "produits" 
        ? `${API_URL}/api/metier/catalogue/produits/import`
        : `${API_URL}/api/metier/catalogue/services/import`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify({ items })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`${result.imported || items.length} éléments importés`);
        loadCatalogue();
        setShowImportDialog(false);
        setImportFile(null);
      } else {
        const err = await res.json();
        toast.error(messageErreurValidation(err.detail, "Erreur lors de l'import"));
      }
    } catch (error) {
      console.error("Erreur import:", error);
      toast.error("Erreur lors de l'import du fichier");
    } finally {
      setImporting(false);
    }
  };

  // Catégories de PRODUITS (équipements uniquement)
  const CATEGORIES_PRODUIT = catProduitItems.map(c => ({ value: c.id, label: c.label }));
  const CATEGORIES_SERVICE = catServiceItems.map(c => ({ value: c.id, label: c.label }));
  const TYPES_PRODUIT = typesProduitItems.map(c => ({ value: c.id, label: c.label }));
  const TYPES_SERVICE = typesServiceItems.map(c => ({ value: c.id, label: c.label }));

  // Fiches CEE courantes (pour autocomplétion)
  // Note: fichesCEE est chargé depuis l'API /api/metier/aides/fiches-cee

  // Options de filtre catégorie : la liste configurée du tenant ; si elle est VIDE (ex. mario_bat,
  // `categories_produit` non seedée), les catégories réellement présentes dans les données.
  const optionsCategorie = (liste, donnees) => liste.length
    ? liste
    : [...new Set(donnees.map(d => d.categorie).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const CATEGORIES_FILTRE_PRODUITS = optionsCategorie(CATEGORIES_PRODUIT, produits);
  const CATEGORIES_FILTRE_SERVICES = optionsCategorie(CATEGORIES_PRODUIT, services);
  const categories = CATEGORIES_FILTRE_PRODUITS;

  // Filtrer les éléments
  const filteredProduits = produits.filter(p => {
    const matchSearch = searchTerm === "" || 
      p.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.marque?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategorie = filterCategorie === "all" || p.categorie === filterCategorie;
    return matchSearch && matchCategorie;
  });

  const filteredServices = services.filter(s => {
    const matchSearch = searchTerm === "" ||
      s.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategorie = filterCategorie === "all" || s.categorie === filterCategorie;
    return matchSearch && matchCategorie;
  });

  const filteredPacks = packs.filter(p => 
    p.nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Formatter le prix
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price || 0);
  };

  // === ACTIONS GROUPÉES PRODUITS ===
  const handleBulkDeleteProduits = async (ids) => {
    if (!ids || ids.length === 0) {
      toast.error("Aucun produit sélectionné");
      return;
    }

    if (!window.confirm(`Supprimer ${ids.length} produit(s) ?`)) return;

    let deleted = 0;
    let errors = 0;

    for (const id of ids) {
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/produits/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(tenantId),
          credentials: 'include'
        });
        if (res.ok) deleted++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (deleted > 0) {
      toast.success(`${deleted} produit(s) supprimé(s)`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s) lors de la suppression`);
    }
  };

  const handleBulkToggleProduits = async (actif, ids) => {
    if (!ids || ids.length === 0) {
      toast.error("Aucun produit sélectionné");
      return;
    }

    let updated = 0;
    let errors = 0;

    for (const id of ids) {
      const produit = produits.find(p => p.id === id);
      if (!produit) continue;
      
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/produits/${id}`, {
          method: "PUT",
          headers: getAuthHeaders(tenantId),
          credentials: 'include',
          body: JSON.stringify({ ...produit, actif })
        });
        if (res.ok) updated++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (updated > 0) {
      toast.success(`${updated} produit(s) ${actif ? 'activé(s)' : 'désactivé(s)'}`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s)`);
    }
  };

  // === ACTIONS GROUPÉES SERVICES ===
  const handleBulkDeleteServices = async (ids) => {
    if (!ids || ids.length === 0) {
      toast.error("Aucun service sélectionné");
      return;
    }

    if (!window.confirm(`Supprimer ${ids.length} service(s) ?`)) return;

    let deleted = 0;
    let errors = 0;

    for (const id of ids) {
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/services/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(tenantId),
          credentials: 'include'
        });
        if (res.ok) deleted++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (deleted > 0) {
      toast.success(`${deleted} service(s) supprimé(s)`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s) lors de la suppression`);
    }
  };

  const handleBulkToggleServices = async (actif, ids) => {
    if (!ids || ids.length === 0) {
      toast.error("Aucun service sélectionné");
      return;
    }

    let updated = 0;
    let errors = 0;

    for (const id of ids) {
      const service = services.find(s => s.id === id);
      if (!service) continue;
      
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/services/${id}`, {
          method: "PUT",
          headers: getAuthHeaders(tenantId),
          credentials: 'include',
          body: JSON.stringify({ ...service, actif })
        });
        if (res.ok) updated++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (updated > 0) {
      toast.success(`${updated} service(s) ${actif ? 'activé(s)' : 'désactivé(s)'}`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s)`);
    }
  };

  // Ouvrir dialog création/édition produit
  const openProduitDialog = (produit = null) => {
    setEditingItem(produit);
    // Initialiser formData avec toutes les valeurs nécessaires
    const defaultValues = {
      reference: "",
      designation: "",
      description: "",
      description_html: "",
      commentaire: "",
      fiche_cee: "",
      prix_ht: 0,
      taux_tva: 5.5,
      unite: "u",
      type_produit: "equipement",
      categorie: "divers",
      activite: "climat",
      marque: "",
      modele: "",
      puissance_kw: null,
      cop: null,
      scop: null,
      etas: null,
      classe_energie: "",
      actif: true
    };
    // Fusionner avec le produit existant pour préserver tous les champs
    // 🔑 N°980 — les caractéristiques s'éditent en LIGNES (clé/valeur) ; le dict
    //    stocké n'est reconstruit qu'à l'enregistrement.
    setFormData({
      ...(produit ? { ...defaultValues, ...produit } : defaultValues),
      caracteristiques_lignes: caracteristiquesVersLignes(produit?.caracteristiques),
    });
    setShowProduitDialog(true);
  };

  // Ouvrir dialog création/édition service
  const openServiceDialog = (service = null) => {
    setEditingItem(service);
    // Initialiser formData avec toutes les valeurs nécessaires
    const defaultValues = {
      reference: "",
      designation: "",
      description: "",
      description_html: "",
      fiche_cee: "",
      prix_ht: 0,
      taux_tva: 10,
      unite: "forfait",
      type_service: "main_oeuvre",
      categorie: "installation",
      activite: "climat",
      duree_estimee_heures: null,
      actif: true
    };
    // Fusionner avec le service existant pour préserver tous les champs
    setFormData(service ? { ...defaultValues, ...service } : defaultValues);
    setShowServiceDialog(true);
  };

  // Sauvegarder produit
  const saveProduit = async () => {
    try {
      const url = editingItem?.id 
        ? `${API_URL}/api/metier/catalogue/produits/${editingItem.id}`
        : `${API_URL}/api/metier/catalogue/produits`;
      
      // Nettoyer les valeurs "none" en null et convertir les champs
      const cleanedData = {
        // 🔑 N°890 — OMISE si vide : le backend l'ATTRIBUE par la séquence du
        // tenant. ⛔ Jamais `""` — c'était exactement le déclencheur du 422.
        ...refSaisieOuOmise(formData.reference),
        designation: formData.designation,
        description: formData.description || null,
        description_html: formData.description_html || null,
        commentaire: formData.commentaire || null,
        fiche_cee: formData.fiche_cee === "none" ? null : (formData.fiche_cee || null),
        classe_energie: formData.classe_energie === "none" ? null : (formData.classe_energie || null),
        prix_ht: parseFloat(formData.prix_ht) || 0,
        taux_tva: parseFloat(formData.taux_tva) || 5.5,
        unite: formData.unite || "u",
        type_produit: formData.type_produit || "equipement",
        categorie: formData.categorie || "divers",
        activite: formData.activite || "climat",
        marque: formData.marque || null,
        modele: formData.modele || null,
        puissance_kw: formData.puissance_kw ? parseFloat(formData.puissance_kw) : null,
        cop: formData.cop ? parseFloat(formData.cop) : null,
        scop: formData.scop ? parseFloat(formData.scop) : null,
        etas: formData.etas ? parseFloat(formData.etas) : null,
        actif: formData.actif !== false
      };

      // 🔴 N°980 — caractéristiques saisies : refusées AVANT envoi si une ligne
      //    est incomplète ou en double (⛔ rien écarté en silence).
      const carac = lignesVersCaracteristiques(formData.caracteristiques_lignes);
      if (carac.erreurs.length) {
        toast.error(`Caractéristiques : ${carac.erreurs.join(" · ")}`);
        return;
      }
      cleanedData.caracteristiques = carac.caracteristiques;
      
      // ⛔ REFUS AVANT L'ENVOI : inutile d'aller chercher un 422 que l'on sait
      // certain. L'utilisateur voit le champ à remplir, ⛔ pas un échec opaque.
      const manques = champsObligatoiresManquants(cleanedData);
      if (manques.length) {
        toast.error(`À renseigner : ${manques.join(", ")}`);
        return;
      }
      console.log("[Catalogue] Saving produit:", { url, method: editingItem?.id ? "PUT" : "POST", cleanedData });
      
      const response = await fetch(url, {
        method: editingItem?.id ? "PUT" : "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        toast.success(editingItem?.id ? "Produit modifié" : "Produit créé");
        setShowProduitDialog(false);
        loadCatalogue();
      } else {
        const err = await response.json();
        console.error("[Catalogue] Save produit error:", response.status, err);
        toast.error(messageErreurValidation(err.detail, "Erreur lors de l'enregistrement"));
      }
    } catch (error) {
      console.error("[Catalogue] Save produit exception:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Sauvegarder service
  const saveService = async () => {
    try {
      const url = editingItem?.id 
        ? `${API_URL}/api/metier/catalogue/services/${editingItem.id}`
        : `${API_URL}/api/metier/catalogue/services`;
      
      // Nettoyer les données - s'assurer que categorie est valide
      const validCategories = ['installation', 'mise_en_service', 'maintenance', 'déposé', 'raccordement', 'prestations', 'divers'];
      const cleanedData = {
        reference: formData.reference,
        designation: formData.designation,
        description: formData.description || null,
        description_html: formData.description_html || null,
        prix_ht: parseFloat(formData.prix_ht) || 0,
        taux_tva: parseFloat(formData.taux_tva) || 10,
        unite: formData.unite || "forfait",
        categorie: validCategories.includes(formData.categorie) ? formData.categorie : 'divers',
        fiche_cee: formData.fiche_cee === "none" ? null : (formData.fiche_cee || null),
        duree_estimee_heures: formData.duree_estimee_heures ? parseFloat(formData.duree_estimee_heures) : null,
        actif: formData.actif !== false
      };
      
      console.log("[Catalogue] Saving service:", { url, method: editingItem?.id ? "PUT" : "POST", cleanedData });
      
      const response = await fetch(url, {
        method: editingItem?.id ? "PUT" : "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        toast.success(editingItem?.id ? "Service modifié" : "Service créé");
        setShowServiceDialog(false);
        loadCatalogue();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("[Catalogue] Save service error:", response.status, errorData);
        toast.error(messageErreurValidation(errorData.detail, "Erreur lors de l'enregistrement"));
      }
    } catch (error) {
      console.error("[Catalogue] Save service exception:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Toggle actif/inactif
  const toggleActif = async (type, item) => {
    try {
      const url = `${API_URL}/api/metier/catalogue/${type}/${item.id}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify({ ...item, actif: !item.actif })
      });

      if (response.ok) {
        toast.success(`${item.designation || item.nom} ${!item.actif ? 'activé' : 'désactivé'}`);
        loadCatalogue();
      }
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  // Supprimer un élément du catalogue
  const deleteItem = async (type, item) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer "${item.designation || item.nom}" ?`)) {
      return;
    }
    
    try {
      const url = `${API_URL}/api/metier/catalogue/${type}/${item.id}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(tenantId),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`${item.designation || item.nom} supprimé`);
        loadCatalogue();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Synchroniser avec Pennylane
  const [syncingPennylane, setSyncingPennylane] = useState(false);
  
  const syncWithPennylane = async () => {
    setSyncingPennylane(true);
    try {
      const response = await fetch(`${API_URL}/api/pennylane/sync-catalog`, {
        method: "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Synchronisation réussie: ${data.synced || 0} mis à jour, ${data.created || 0} créés`);
        loadCatalogue();
      } else {
        toast.error(data.detail || "Erreur de synchronisation");
      }
    } catch (error) {
      console.error("[Catalogue] Sync Pennylane error:", error);
      toast.error("Erreur lors de la synchronisation avec Pennylane");
    } finally {
      setSyncingPennylane(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--oh-success-text)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="catalogue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)]">Catalogue</h1>
          <p className="text-[var(--oh-text-secondary)]">Gérez vos produits, services et packs</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Sync Pennylane = facturation AMO (TEF). Masqué pour les profils
              installateur (OMV/Climat) qui n'utilisent pas ce flux.
              ET réservé au tenant qui DÉCLARE l'intégration Pennylane
              (feature flag integration.pennylane) — mario_bat n'en a pas. */}
          {!isOmv && hasIntegrationPennylane() && (
            <Button
              data-testid="sync-pennylane-btn"
              variant="outline"
              onClick={syncWithPennylane}
              disabled={syncingPennylane}
              className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
            >
              {syncingPennylane ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Pennylane
            </Button>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--oh-text-secondary)]" />
            <Input
              data-testid="catalogue-search"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
          </div>
          <Select value={filterCategorie} onValueChange={setFilterCategorie}>
            <SelectTrigger className="w-full max-w-[180px] bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[var(--oh-accent-bg)]">
              <Package className="h-6 w-6 text-[var(--oh-accent-text)]" />
            </div>
            <div>
              <p className="text-2xl font-medium text-[var(--oh-text)]">{produits.length}</p>
              <p className="text-sm text-[var(--oh-text-secondary)]">Produits</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[var(--oh-accent-bg)]">
              <Wrench className="h-6 w-6 text-[var(--oh-accent-text)]" />
            </div>
            <div>
              <p className="text-2xl font-medium text-[var(--oh-text)]">{services.length}</p>
              <p className="text-sm text-[var(--oh-text-secondary)]">Services</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--oh-bg-elevated)]">
          <TabsTrigger value="produits" className="data-[state=active]:bg-[var(--oh-accent-bg)]">
            <Package className="h-4 w-4 mr-2" />
            Produits ({filteredProduits.length})
          </TabsTrigger>
          <TabsTrigger value="services" className="data-[state=active]:bg-[var(--oh-accent-bg)]">
            <Wrench className="h-4 w-4 mr-2" />
            Services ({filteredServices.length})
          </TabsTrigger>
          {/* 🔴 MISSIONS AMO — VISIBLE SI LE TENANT DÉCLARE LA CAPABILITÉ.
              ⚠️ Ce test valait `!isOmv` : « tout le monde SAUF OMV ». Mario,
              ni TEF ni OMV, héritait d'un concept AMO/MaPrimeAdapt' qui ⛔ ne
              le concerne PAS — il le voyait à l'écran (mesuré le 17/09).
              🔑 Déclaration POSITIVE, servie par `/user/tenants`, alignée sur
              l'autorité backend (module AMO). ⛔ Pas `!Mario`, ⛔ pas une liste
              d'exclusions : la prochaine arrivée serait encore oubliée. */}
          {peutVoirMissionsAmo && (
            <TabsTrigger value="missions_amo" className="data-[state=active]:bg-[var(--oh-info-bg)]">
              <Euro className="h-4 w-4 mr-2" />
              Missions AMO
            </TabsTrigger>
          )}
          {/* Packs masqués pour l'instant
          <TabsTrigger value="packs" className="data-[state=active]:bg-[var(--oh-success-bg)]">
            <Boxes className="h-4 w-4 mr-2" />
            Packs ({filteredPacks.length})
          </TabsTrigger>
          */}
        </TabsList>

        {/* Produits Tab */}
        <TabsContent value="produits" className="mt-4">
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)]">Produits</CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-[var(--oh-border)]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter CSV
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowImportDialog(true)}
                    className="border-[var(--oh-border)]"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importer CSV
                  </Button>
                  <Button 
                    data-testid="add-produit-btn"
                    onClick={() => openProduitDialog()}
                    className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau produit
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <OHGrid
                data={filteredProduits}
                testIdPrefix="catalogue-produits"
                defaultSortKey="created_at"
                defaultSortDir="desc"
                pagination
                selectable
                emptyMessage="Aucun produit"
                columns={[
                  { key: "reference", label: "Référence", filter: { type: "text", placeholder: "Réf…" },
                    render: (v) => <span className="font-mono text-xs" style={{ color: "var(--oh-info-text)" }}>{v || "-"}</span> },
                  { key: "designation", label: "Désignation", filter: { type: "text", placeholder: "Nom…" },
                    render: (v, row) => (
                      <div>
                        <span style={{ fontWeight: 500 }}>{v}</span>
                        {row.commentaire && (
                          <div data-testid={`produit-commentaire-${row.id}`} className="text-xs mt-1 max-w-[420px] truncate"
                               style={{ color: "var(--oh-text-secondary)" }} title={row.commentaire}>{row.commentaire}</div>
                        )}
                      </div>
                    ) },
                  { key: "marque", label: "Marque", filter: { type: "text", placeholder: "Marque…" }, render: (v) => v || "-" },
                  // 🔴 OMV-12 — INFORMATIONS TECHNIQUES VISIBLES (specs extraites des fiches).
                  { key: "caracteristiques", label: "Caractéristiques", sortable: false,
                    render: (v) => {
                      const items = caracteristiquesListe(v);
                      if (!items.length) return <span style={{ color: "var(--oh-text-muted)" }}>-</span>;
                      const full = items.map((i) => `${i.label} : ${i.texte}`).join(" · ");
                      return (
                        <div className="flex flex-wrap gap-1 max-w-[320px]" title={full} data-testid="produit-caracteristiques">
                          {items.slice(0, 4).map((i, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs font-normal"
                              style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}>
                              {i.label} : {i.texte}
                            </Badge>
                          ))}
                          {items.length > 4 && <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>+{items.length - 4}</span>}
                        </div>
                      );
                    } },
                  { key: "fiche_cee", label: "Fiche CEE", filter: { type: "text", placeholder: "Fiche…" },
                    render: (v) => v ? <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs font-mono">{v}</Badge> : "-" },
                  { key: "categorie", label: "Catégorie", filter: { type: "select", options: CATEGORIES_FILTRE_PRODUITS },
                    render: (v) => CATEGORIES_PRODUIT.find(c => c.value === v)?.label || v || "-" },
                  { key: "prix_ht", label: "Prix HT", className: "text-right",
                    render: (v) => <span style={{ color: "var(--oh-success-text)" }}>{formatPrice(v)}</span> },
                  { key: "actif", label: "Statut", sortable: false, className: "text-center",
                    filter: { type: "select", value: (row) => row.actif ? "actif" : "inactif",
                              options: [{ value: "actif", label: "Actif" }, { value: "inactif", label: "Inactif" }] },
                    render: (v) => <Badge variant={v ? "success" : "secondary"}>{v ? "Actif" : "Inactif"}</Badge> },
                ]}
                rowActions={[
                  { label: "Activer", show: (row) => !row.actif, onClick: (row) => toggleActif("produits", row) },
                  { label: "Désactiver", show: (row) => row.actif, onClick: (row) => toggleActif("produits", row) },
                  { label: "Modifier", onClick: (row) => openProduitDialog(row) },
                  { label: "Supprimer", className: "text-[var(--oh-error-text)]", onClick: (row) => deleteItem("produits", row) },
                ]}
                bulkActions={[
                  { label: (n) => `Activer (${n})`, onClick: (ids) => handleBulkToggleProduits(true, ids) },
                  { label: (n) => `Désactiver (${n})`, onClick: (ids) => handleBulkToggleProduits(false, ids) },
                  { label: (n) => `Supprimer (${n})`, variant: "destructive", onClick: (ids) => handleBulkDeleteProduits(ids) },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-4">
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)]">Services (Main d'œuvre)</CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-[var(--oh-border)]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter CSV
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowImportDialog(true)}
                    className="border-[var(--oh-border)]"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importer CSV
                  </Button>
                  <Button 
                    data-testid="add-service-btn"
                    onClick={() => openServiceDialog()}
                    className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau service
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <OHGrid
                data={filteredServices}
                testIdPrefix="catalogue-services"
                defaultSortKey="created_at"
                defaultSortDir="desc"
                pagination
                selectable
                emptyMessage="Aucun service"
                columns={[
                  { key: "reference", label: "Référence", filter: { type: "text", placeholder: "Réf…" },
                    render: (v) => <span className="font-mono text-xs" style={{ color: "var(--oh-info-text)" }}>{v || "-"}</span> },
                  { key: "designation", label: "Désignation", filter: { type: "text", placeholder: "Nom…" },
                    render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
                  { key: "categorie", label: "Catégorie", filter: { type: "select", options: CATEGORIES_FILTRE_SERVICES },
                    render: (v) => CATEGORIES_PRODUIT.find(c => c.value === v)?.label || v || "-" },
                  { key: "prix_ht", label: "Prix HT", className: "text-right",
                    render: (v) => <span style={{ color: "var(--oh-success-text)" }}>{formatPrice(v)}</span> },
                  { key: "unite", label: "Unité", sortable: false, className: "text-center", render: (v) => v || "u" },
                  { key: "actif", label: "Statut", sortable: false, className: "text-center",
                    filter: { type: "select", value: (row) => row.actif ? "actif" : "inactif",
                              options: [{ value: "actif", label: "Actif" }, { value: "inactif", label: "Inactif" }] },
                    render: (v) => <Badge variant={v ? "success" : "secondary"}>{v ? "Actif" : "Inactif"}</Badge> },
                ]}
                rowActions={[
                  { label: "Activer", show: (row) => !row.actif, onClick: (row) => toggleActif("services", row) },
                  { label: "Désactiver", show: (row) => row.actif, onClick: (row) => toggleActif("services", row) },
                  { label: "Modifier", onClick: (row) => openServiceDialog(row) },
                  { label: "Supprimer", className: "text-[var(--oh-error-text)]", onClick: (row) => deleteItem("services", row) },
                ]}
                bulkActions={[
                  { label: (n) => `Activer (${n})`, onClick: (ids) => handleBulkToggleServices(true, ids) },
                  { label: (n) => `Désactiver (${n})`, onClick: (ids) => handleBulkToggleServices(false, ids) },
                  { label: (n) => `Supprimer (${n})`, variant: "destructive", onClick: (ids) => handleBulkDeleteServices(ids) },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packs Tab */}
        {/* Missions AMO Tab */}
        <TabsContent value="missions_amo" className="mt-4">
          <MissionsAMOTab apiUrl={API_URL} tenantId={tenantId} getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="packs" className="mt-4">
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)]">Packs / Templates de devis</CardTitle>
              <Button 
                data-testid="add-pack-btn"
                disabled
                className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] opacity-50" /* charte-ok: bouton primaire — accent métier */
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau pack (bientôt)
              </Button>
            </CardHeader>
            <CardContent>
              {filteredPacks.length === 0 ? (
                <div className="text-center py-8 text-[var(--oh-text-secondary)]">
                  <Boxes className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun pack configuré</p>
                  <p className="text-sm mt-2">Les packs permettent de créer des devis pré-configurés</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Nom</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Produits</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Services</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPacks.map((pack) => (
                      <TableRow key={pack.id} className="border-[var(--oh-border)]">
                        <TableCell className="text-[var(--oh-text)] font-medium">{pack.nom}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)]">{pack.produits?.length || 0}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)]">{pack.services?.length || 0}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" disabled>
                            <Edit className="h-4 w-4 text-[var(--oh-text-secondary)]" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Produit */}
      <Dialog open={showProduitDialog} onOpenChange={setShowProduitDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingItem?.id ? "Modifier le produit" : "Nouveau produit"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Section: Identification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Identification</h4>
              
              {/* Référence fabricant */}
              <div className="space-y-2 mb-3">
                <label className="text-sm text-[var(--oh-info-text)] font-medium">Référence fabricant *</label>
                <Input
                  data-testid="produit-reference-input"
                  value={formData.reference || ""}
                  onChange={(e) => setFormData({...formData, reference: e.target.value.toUpperCase()})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-info-border)] focus:border-[var(--oh-info-border)]"
                  placeholder="ALTHERMA-3-11KW, CALYPSO-VS-270, etc."
                />
                <p className="text-xs text-[var(--oh-text-secondary)]">Référence unique du produit chez le fabricant</p>
              </div>

              {/* Marque et Modèle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Marque</label>
                  <Input
                    value={formData.marque || ""}
                    onChange={(e) => setFormData({...formData, marque: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Atlantic, Daikin, Mitsubishi..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Modèle</label>
                  <Input
                    value={formData.modele || ""}
                    onChange={(e) => setFormData({...formData, modele: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Altherma 3, Calypso VS..."
                  />
                </div>
              </div>

              {/* Désignation */}
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Désignation *</label>
                <Input
                  data-testid="produit-designation-input"
                  value={formData.designation || ""}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="PAC Air/Eau 11kW Haute température"
                />
              </div>
            </div>
            
            {/* Section: Fiche CEE — climatpac + omv_france (CC-DEV1-27 : dé-gaté OMV pour
                lier un produit à une fiche CEE. Le lien produit↔opération côté devis viendra
                après décision métier sur le prix). */}
            {(tenantId === "climatpac" || tenantId === "omv_france") && (
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] mb-3">Fiche Opération CEE</h4>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Code fiche CEE</label>
                <Select
                  value={formData.fiche_cee || "none"}
                  onValueChange={(value) => setFormData({...formData, fiche_cee: value === "none" ? null : value})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue placeholder="Sélectionner une fiche CEE" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {fichesCEE.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[var(--oh-text-secondary)]">Fiche opération standardisée (ex: BAR-TH-148 pour ballon thermodynamique)</p>
              </div>
            </div>
            )}

            {/* Section: Classification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Classification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Type *</label>
                  <Select
                    value={formData.type_produit || "equipement"}
                    onValueChange={(value) => setFormData({...formData, type_produit: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_PRODUIT.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Catégorie *</label>
                  <Select
                    value={formData.categorie || "divers"}
                    onValueChange={(value) => setFormData({...formData, categorie: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="produit-categorie-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES_PRODUIT.map(c => (
                        <SelectItem key={c.value} value={c.value} data-testid={`produit-categorie-opt-${c.value}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section: Prix */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-stat-cyan-text)] mb-3">Tarification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Prix HT *</label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--oh-text-secondary)]" />
                    <Input
                      data-testid="produit-prix-input"
                      type="number"
                      step="0.01"
                      value={formData.prix_ht || 0}
                      onChange={(e) => setFormData({...formData, prix_ht: parseFloat(e.target.value)})}
                      className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">TVA</label>
                  <Select
                    value={String(formData.taux_tva || 5.5)}
                    onValueChange={(value) => setFormData({...formData, taux_tva: parseFloat(value)})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5.5">5.5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Unité</label>
                  <Select
                    value={formData.unite || "u"}
                    onValueChange={(value) => setFormData({...formData, unite: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="u">Unité</SelectItem>
                      <SelectItem value="m²">m²</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="kit">Kit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Section: Caractéristiques techniques — CONDITIONNELLES au type/catégorie (CC-DEV1-27).
                VMC → débit d'air / nb caissons / puissance ; isolation → résistance R ; PAC (et
                défaut) → puissance / COP / SCOP / classe. Une VMC n'affiche PLUS de COP/SCOP. */}
            {(() => {
              const _catLabel = (CATEGORIES_PRODUIT.find(c => c.value === formData.categorie)?.label) || "";
              const _catStr = `${formData.categorie || ""} ${_catLabel}`.toLowerCase();
              const isVmc = /vmc|ventil/.test(_catStr);
              const isIsolation = /isol/.test(_catStr);
              const numOrNull = (v) => (v === "" || v == null ? null : parseFloat(v));
              const intOrNull = (v) => (v === "" || v == null ? null : parseInt(v, 10));
              return (
                <div>
                  <h4 className="text-sm font-medium text-[var(--oh-info-text)] mb-3">Caractéristiques techniques</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    {isVmc ? (<>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Débit d'air (m³/h)</label>
                        <Input type="number" step="1" data-testid="produit-debit-air"
                          value={formData.debit_air_m3h || ""}
                          onChange={(e) => setFormData({...formData, debit_air_m3h: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="1500" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Nb caissons</label>
                        <Input type="number" step="1" data-testid="produit-nb-caissons"
                          value={formData.nb_caissons || ""}
                          onChange={(e) => setFormData({...formData, nb_caissons: intOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="1" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Puissance (kW)</label>
                        <Input type="number" step="0.1"
                          value={formData.puissance_kw || ""}
                          onChange={(e) => setFormData({...formData, puissance_kw: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="0.5" />
                      </div>
                    </>) : isIsolation ? (
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Résistance thermique R (m².K/W)</label>
                        <Input type="number" step="0.05" data-testid="produit-resistance-r"
                          value={formData.resistance_thermique_r || ""}
                          onChange={(e) => setFormData({...formData, resistance_thermique_r: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="7.0" />
                      </div>
                    ) : (<>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Puissance (kW)</label>
                        <Input type="number" step="0.1"
                          value={formData.puissance_kw || ""}
                          onChange={(e) => setFormData({...formData, puissance_kw: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="11" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">COP</label>
                        <Input type="number" step="0.01" data-testid="produit-cop"
                          value={formData.cop || ""}
                          onChange={(e) => setFormData({...formData, cop: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="4.5" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">SCOP</label>
                        <Input type="number" step="0.01" data-testid="produit-scop"
                          value={formData.scop || ""}
                          onChange={(e) => setFormData({...formData, scop: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="4.2" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Classe énergie</label>
                        <Select value={formData.classe_energie || ""}
                          onValueChange={(value) => setFormData({...formData, classe_energie: value})}>
                          <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            <SelectItem value="A+++">A+++</SelectItem>
                            <SelectItem value="A++">A++</SelectItem>
                            <SelectItem value="A+">A+</SelectItem>
                            <SelectItem value="A">A</SelectItem>
                            <SelectItem value="B">B</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>)}
                  </div>
                </div>
              );
            })()}
            
            {/* Section: Commentaire libre du produit */}
            <div className="space-y-2">
              <label htmlFor="produit-commentaire-input" className="text-sm text-[var(--oh-text-secondary)]">Commentaire</label>
              <Textarea
                id="produit-commentaire-input"
                data-testid="produit-commentaire-input"
                aria-label="Commentaire du produit"
                maxLength={1000}
                rows={3}
                value={formData.commentaire || ""}
                onChange={(e) => setFormData({...formData, commentaire: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Précision libre sur ce produit (visible à la réouverture de la fiche)"
              />
            </div>

            {/* 🔴 N°980 — Caractéristiques techniques ÉDITABLES (clé/valeur).
                Libellés proposés depuis la source unique CARACT_LABELS. */}
            <div data-testid="produit-caracteristiques-editeur">
              <h4 className="text-sm font-medium mb-2" style={{ color: "var(--oh-text)" }}>
                Caractéristiques techniques
              </h4>
              <datalist id="caracteristiques-connues">
                {Object.entries(CARACT_LABELS).map(([cle, lib]) => (
                  <option key={cle} value={lib} />
                ))}
              </datalist>
              {(formData.caracteristiques_lignes || []).map((l, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                  <Input
                    list="caracteristiques-connues"
                    value={l.cle}
                    placeholder="Caractéristique (ex. Débit d'air)"
                    aria-label={`Nom de la caractéristique ${idx + 1}`}
                    data-testid={`produit-carac-cle-${idx}`}
                    onChange={(e) => setFormData((f) => ({
                      ...f,
                      caracteristiques_lignes: f.caracteristiques_lignes.map((x, k) =>
                        k === idx ? { ...x, cle: e.target.value } : x),
                    }))}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  />
                  <Input
                    value={l.valeur}
                    placeholder="Valeur (ex. 450)"
                    aria-label={`Valeur de la caractéristique ${idx + 1}`}
                    data-testid={`produit-carac-valeur-${idx}`}
                    onChange={(e) => setFormData((f) => ({
                      ...f,
                      caracteristiques_lignes: f.caracteristiques_lignes.map((x, k) =>
                        k === idx ? { ...x, valeur: e.target.value } : x),
                    }))}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  />
                  <Button
                    type="button" variant="ghost" size="sm"
                    aria-label={`Retirer la caractéristique ${idx + 1}`}
                    data-testid={`produit-carac-retirer-${idx}`}
                    onClick={() => setFormData((f) => ({
                      ...f,
                      caracteristiques_lignes: f.caracteristiques_lignes.filter((_, k) => k !== idx),
                    }))}
                    style={{ color: "var(--oh-text-secondary)" }}
                  >
                    Retirer
                  </Button>
                </div>
              ))}
              <Button
                type="button" variant="outline" size="sm"
                data-testid="produit-carac-ajouter"
                onClick={() => setFormData((f) => ({
                  ...f,
                  caracteristiques_lignes: [...(f.caracteristiques_lignes || []), { cle: "", valeur: "" }],
                }))}
                style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}
              >
                Ajouter une caractéristique
              </Button>
              {editingItem?.source_sha256 && (
                <p className="text-xs mt-2" style={{ color: "var(--oh-text-muted)" }}>
                  Valeurs d'origine tracées{editingItem?.source_page ? ` — page ${editingItem.source_page}` : ""} ·
                  sha256 {String(editingItem.source_sha256).slice(0, 10)}… · une valeur modifiée
                  ici n'est plus couverte par cette source.
                </p>
              )}
            </div>

            {/* Section: Informations complémentaires (Éditeur intelligent) */}
            <div>
              <h4 className="text-sm font-medium text-pink-400 mb-3">Informations complémentaires</h4>
              <p className="text-xs text-[var(--oh-text-secondary)] mb-2">
                Ajoutez des informations détaillées : caractéristiques, conditions, garanties, etc.
              </p>
              <SmartLegalEditor
                value={formData.description_html || ""}
                onChange={(html) => setFormData({...formData, description_html: html})}
                placeholder="Collez ou saisissez des informations complémentaires sur ce produit..."
                minHeight="120px"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProduitDialog(false)}>
              Annuler
            </Button>
            <Button 
              data-testid="save-produit-btn"
              onClick={saveProduit} 
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
            >
              {editingItem?.id ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Service */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingItem?.id ? "Modifier le service" : "Nouveau service"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Section: Identification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Identification</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-info-text)] font-medium">Référence *</label>
                  <Input
                    data-testid="service-reference-input"
                    value={formData.reference || ""}
                    onChange={(e) => setFormData({...formData, reference: e.target.value.toUpperCase()})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-info-border)] focus:border-[var(--oh-info-border)]"
                    placeholder="MO-INST-PAC, MO-DEPOSE, etc."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Désignation *</label>
                  <Input
                    data-testid="service-designation-input"
                    value={formData.designation || ""}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Installation PAC Air/Eau complète"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Description</label>
                  <Input
                    value={formData.description || ""}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Inclut mise en service, raccordements..."
                  />
                </div>
              </div>
            </div>
            
            {/* Section: Classification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Classification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Type</label>
                  <Select
                    value={formData.type_service || "main_oeuvre"}
                    onValueChange={(value) => setFormData({...formData, type_service: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_SERVICE.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Catégorie</label>
                  <Select
                    value={formData.categorie || "installation"}
                    onValueChange={(value) => setFormData({...formData, categorie: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES_SERVICE.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Fiche CEE associée — climatpac only (CC-Catalogue 2026-05-05) */}
              {tenantId === "climatpac" && (
                <div className="space-y-2 mt-3">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Fiche CEE associée (optionnel)</label>
                  <Select
                    value={formData.fiche_cee || "none"}
                    onValueChange={(value) => setFormData({...formData, fiche_cee: value === "none" ? null : value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue placeholder="Aucune" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {fichesCEE.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Section: Tarification */}
            <div>
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] mb-3">Tarification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Prix HT *</label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--oh-text-secondary)]" />
                    <Input
                      data-testid="service-prix-input"
                      type="number"
                      step="0.01"
                      value={formData.prix_ht || 0}
                      onChange={(e) => setFormData({...formData, prix_ht: parseFloat(e.target.value)})}
                      className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">TVA</label>
                  <Select
                    value={String(formData.taux_tva || 10)}
                    onValueChange={(value) => setFormData({...formData, taux_tva: parseFloat(value)})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5.5">5.5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Unité</label>
                  <Select
                    value={formData.unite || "forfait"}
                    onValueChange={(value) => setFormData({...formData, unite: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="forfait">Forfait</SelectItem>
                      <SelectItem value="h">Heure</SelectItem>
                      <SelectItem value="jour">Jour</SelectItem>
                      <SelectItem value="u">Unité</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2 mt-3">
                <label className="text-sm text-[var(--oh-text-secondary)]">Durée estimée (heures)</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.duree_estimee_heures || ""}
                  onChange={(e) => setFormData({...formData, duree_estimee_heures: e.target.value ? parseFloat(e.target.value) : null})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] w-32"
                  placeholder="4"
                />
              </div>
            </div>
            
            {/* Section: Informations complémentaires (Éditeur intelligent) */}
            <div>
              <h4 className="text-sm font-medium text-pink-400 mb-3">Informations complémentaires</h4>
              <p className="text-xs text-[var(--oh-text-secondary)] mb-2">
                Détails du service : inclusions, exclusions, conditions, etc.
              </p>
              <SmartLegalEditor
                value={formData.description_html || ""}
                onChange={(html) => setFormData({...formData, description_html: html})}
                placeholder="Collez ou saisissez des informations complémentaires sur ce service..."
                minHeight="120px"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>
              Annuler
            </Button>
            <Button 
              data-testid="save-service-btn"
              onClick={saveService}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
              disabled={!formData.reference || !formData.designation}
            >
              {editingItem?.id ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Import CSV */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[var(--oh-success-text)]" />
              Importer depuis CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-[var(--oh-bg-elevated)] rounded-lg border border-dashed border-[var(--oh-border)]">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-[var(--oh-text-muted)] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-[var(--oh-accent-solid)] file:text-[var(--oh-accent-contrast)] hover:file:bg-[var(--oh-accent-solid)]"
              />
              {importFile && (
                <p className="mt-2 text-sm text-[var(--oh-success-text)]">
                  ✓ {importFile.name}
                </p>
              )}
            </div>
            
            <div className="text-xs text-[var(--oh-text-muted)] space-y-1">
              <p><strong>Format attendu :</strong> CSV avec séparateur point-virgule (;)</p>
              <p><strong>Colonnes {activeTab === "produits" ? "produits" : "services"} :</strong></p>
              <p className="font-mono text-[var(--oh-text-secondary)]">
                {activeTab === "produits" 
                  ? "reference;nom;categorie;marque;modele;prix_ht;taux_tva;fiche_cee;actif;description"
                  : "reference;nom;categorie;prix_ht;taux_tva;unite;actif;description"
                }
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleImportCSV}
              disabled={!importFile || importing}
              className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] hover:opacity-90" /* charte-ok: bouton primaire — accent métier */
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Import...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
